import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import Seo from '../components/Seo';
import PageHero from '../components/PageHero';
import Reveal from '../components/Reveal';
import SectionHeading from '../components/SectionHeading';
import BookingForm from '../components/BookingForm';
import { ArrowRight, Clock, Pin, Starburst } from '../components/Icons';
import { EVENTS, getEvent } from '../data/events';
import { LOCATIONS } from '../data/site';
import './EventDetail.css';

export default function EventDetail() {
  const { slug } = useParams();
  const event = getEvent(slug);

  if (!event) return <Navigate to="/whats-on" replace />;

  const todayIndex = new Date().getDay();
  const onToday = event.day.includes(todayIndex);
  const others = EVENTS.filter((e) => e.id !== event.id);

  return (
    <>
      <Seo
        title={event.name}
        description={`${event.name} at The Local — ${event.when}, ${event.time}. ${event.blurb}`}
        path={`/whats-on/${event.id}`}
      />

      <PageHero
        eyebrow={onToday ? 'On today' : "What's on"}
        title={event.name}
        lead={event.blurb}
        image={event.image}
      >
        <a className="btn btn--light" href="#book">
          <span>Book your table</span>
          <ArrowRight />
        </a>
      </PageHero>

      {/* Facts bar --------------------------------------------------------- */}
      <section className="section--tight evfacts">
        <div className="page evfacts__inner">
          <div className="evfacts__item">
            <Clock size={16} />
            <span>
              <small>When</small>
              <strong>{event.when}</strong>
            </span>
          </div>
          <div className="evfacts__item">
            <Starburst size={16} spokes={12} />
            <span>
              <small>Time</small>
              <strong className="tabular">{event.time}</strong>
            </span>
          </div>
          <div className="evfacts__item">
            <Pin size={16} />
            <span>
              <small>Where</small>
              <strong>{event.branches}</strong>
            </span>
          </div>
          {onToday ? <span className="evfacts__today">On today</span> : null}
        </div>
      </section>

      {/* About ------------------------------------------------------------- */}
      <section className="section">
        <div className="page split split--wide-left">
          <div className="stack stack-md">
            <SectionHeading eyebrow={`About ${event.name}`} title={event.blurb} size="d3" />
            <Reveal as="p" className="body" delay={0.08}>
              {event.body}
            </Reveal>
            <Reveal as="p" className="body" delay={0.14}>
              {event.long}
            </Reveal>
            <Reveal className="evabout__actions" delay={0.2}>
              <Link to="/menus" className="link">
                View menus <ArrowRight />
              </Link>
            </Reveal>
          </div>

          <Reveal className="evabout__media" variant="zoom">
            <div className="frame frame--4x5 frame--hover">
              <img src={event.image} alt={event.name} loading="lazy" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Booking ----------------------------------------------------------- */}
      <section className="section section--deep" id="book">
        <div className="page stack stack-lg">
          <SectionHeading
            eyebrow="Reservations"
            title="Book your table."
            lead={`Tell us when and how many, and we'll hold a table for ${event.name}.`}
          />
          <BookingForm defaultDetails={`Coming in for ${event.name}.`} />
        </div>
      </section>

      {/* More happening ---------------------------------------------------- */}
      <section className="section">
        <div className="page stack stack-lg">
          <div className="evmore__head">
            <SectionHeading eyebrow="More happening" title="The rest of the week." />
            <Reveal delay={0.15}>
              <Link to="/whats-on" className="link">
                See the full week <ArrowRight />
              </Link>
            </Reveal>
          </div>

          <ul className="evmore__grid">
            {others.map((e, i) => (
              <Reveal as="li" key={e.id} delay={i * 0.09}>
                <Link to={`/whats-on/${e.id}`} className="evcard">
                  <div className="frame frame--4x5 frame--hover evcard__media">
                    <img src={e.image} alt="" loading="lazy" />
                  </div>
                  <div className="evcard__body">
                    <h3 className="display d4">{e.name}</h3>
                    <p className="evcard__when tabular">
                      {e.when} · {e.time}
                    </p>
                    <p className="evcard__blurb">{e.blurb}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      {/* Branches ---------------------------------------------------------- */}
      <section className="section--tight section--deep">
        <div className="page evbranches">
          <p className="eyebrow">Running at both branches</p>
          <div className="evbranches__links">
            {LOCATIONS.map((l) => (
              <Link key={l.id} to={`/locations/${l.id}`} className="link">
                {l.name} <ArrowRight />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
